import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { getClientsAdmin, setClientCapabilities, updateClient } from "../../lib/api";
import {
  CLIENT_CAPABILITIES,
  type ClientCapability,
  type ClientRecord,
} from "./types";
import { createClient } from "./lib/clientDirectory";
import { ClientContactsModal } from "./components/ClientContactsModal";
import { ClientForm } from "./components/ClientForm";
import { PortalUsersModal } from "./components/PortalUsersModal";
import { Modal } from "../../components/Modal";
import styles from "./ClientsPage.module.css";

type SortDirection = "asc" | "desc";

/**
 * Clients management screen.
 *
 * Lists every client and lets an administrator grant/revoke the three per-client
 * capabilities (`view_jobs`, `create_work_orders`, `create_quote_requests`). The
 * `client_capabilities` table is the source of truth; each toggle sends a full
 * replacement via `PUT /api/clients/:id/capabilities`.
 *
 * Client and contact creation AND editing are handled here with the same shared
 * components the New Job form uses ({@link ClientForm} and the contacts modal's
 * inline form), backed by the same `POST`/`PATCH /api/clients` endpoints, so the
 * directory behaves identically on both surfaces. Search and name sorting are
 * client-side over the full admin list.
 *
 * Create and edit are both dialogs on the shared {@link Modal}. Create used to
 * be an inline panel and edit a hand-rolled overlay, so one screen offered two
 * different shapes for the same form.
 */
export function ClientsPage() {
  const [clients, setClients] = useState<ClientRecord[] | null>(null);
  // Two errors, because they mean different things: a load failure leaves nothing
  // to show, while a failed toggle must not take the loaded table down with it.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [portalClient, setPortalClient] = useState<ClientRecord | null>(null);
  const [contactsClient, setContactsClient] = useState<ClientRecord | null>(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const loadClients = useCallback(async (): Promise<void> => {
    try {
      const data = await getClientsAdmin();
      setClients(data.clients);
      setLoadError(null);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : "Failed to load clients");
    }
  }, []);

  useEffect(() => {
    let active = true;
    getClientsAdmin()
      .then((data) => {
        if (active) setClients(data.clients);
      })
      .catch((e: unknown) => {
        if (active) setLoadError(e instanceof Error ? e.message : "Failed to load clients");
      });
    return () => {
      active = false;
    };
  }, []);

  const toggleCapability = async (client: ClientRecord, capability: ClientCapability) => {
    const next = new Set(client.capabilities);
    if (next.has(capability)) next.delete(capability);
    else next.add(capability);
    const nextList = [...next];
    setBusyId(client.id);
    setActionError(null);
    try {
      const result = await setClientCapabilities(client.id, nextList);
      setClients((prev) =>
        prev
          ? prev.map((candidate) =>
              candidate.id === client.id ? { ...candidate, capabilities: result.capabilities as ClientCapability[] } : candidate,
            )
          : prev,
      );
    } catch (e: unknown) {
      setActionError(
        e instanceof Error
          ? `${client.name}: ${e.message}`
          : `Failed to update capabilities for ${client.name}`,
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleClientCreated = useCallback((): void => {
    setCreatingClient(false);
    void loadClients();
  }, [loadClients]);

  const handleClientUpdated = useCallback((): void => {
    setEditingClient(null);
    void loadClients();
  }, [loadClients]);

  const visibleClients = useMemo(() => {
    if (!clients) return [];
    const needle = searchQuery.trim().toLowerCase();
    const filtered = needle
      ? clients.filter((client) => client.name.toLowerCase().includes(needle))
      : clients;
    const sorted = [...filtered].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
    return sortDirection === "asc" ? sorted : sorted.reverse();
  }, [clients, searchQuery, sortDirection]);

  // A load failure and a load in progress both keep the page's own chrome, so
  // the screen does not swap between a full-viewport message and a layout.
  if (loadError || !clients) {
    return (
      <div className={styles.root}>
        <div className={styles.head}>
          <div className={styles.headline}>
            <h1 className={styles.title}>Clients</h1>
          </div>
        </div>
        {loadError ? (
          <p className={styles.actionError} role="alert">
            {loadError}
          </p>
        ) : (
          <p className={styles.empty}>Loading clients…</p>
        )}
      </div>
    );
  }

  const hasClients = clients.length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <div className={styles.headline}>
          <h1 className={styles.title}>Clients</h1>
          <p className={styles.lede}>
            Manage the client directory and the capabilities granted to each client organization.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.newClientButton}
            onClick={() => setCreatingClient(true)}
          >
            New client
          </button>
        </div>
      </div>

      {actionError ? (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      ) : null}

      {!hasClients ? (
        <p className={styles.empty}>
          No clients yet. Use the New client button above, or create one from the New Job form.
        </p>
      ) : (
        <>
          <div className={styles.toolbar}>
            <input
              type="search"
              className={styles.search}
              placeholder="Filter by client name…"
              aria-label="Filter clients by name"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
            />
            <button
              type="button"
              className={styles.sortButton}
              aria-label={`Sort by name, currently ${
                sortDirection === "asc" ? "ascending" : "descending"
              }`}
              onClick={() =>
                setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"))
              }
            >
              Name
              <span className={styles.sortDirection} aria-hidden="true">
                {sortDirection === "asc" ? "↑" : "↓"}
              </span>
            </button>
            <span className={styles.count} role="status">
              {visibleClients.length} of {clients.length} client
              {clients.length === 1 ? "" : "s"}
            </span>
          </div>
          {visibleClients.length === 0 ? (
            <p className={styles.empty}>No clients match “{searchQuery.trim()}”.</p>
          ) : (
            <div className={styles.tableFrame}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Client</th>
                    <th scope="col">Status</th>
                    <th scope="col">Capabilities</th>
                    <th scope="col">Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleClients.map((client) => (
                    <Fragment key={client.id}>
                      <tr>
                        <td data-label="Client">
                        <div className={styles.clientCell}>
                          <span className={styles.clientName}>{client.name}</span>
                          <span className={styles.clientMeta}>
                            {client.email || client.phone || "—"}
                          </span>
                          <span className={styles.clientMeta}>
                            {client.type}
                            {client.abnNumber ? ` · ABN ${client.abnNumber}` : ""}
                            {client.acnNumber ? ` · ACN ${client.acnNumber}` : ""}
                          </span>
                        </div>
                      </td>
                      <td data-label="Status">
                        <span className={styles.status}>{client.status}</span>
                      </td>
                      <td data-label="Capabilities">
                        <div className={styles.capList}>
                          {CLIENT_CAPABILITIES.map((capability) => (
                            <label
                              key={capability.value}
                              className={styles.capToggle}
                              title={capability.description}
                            >
                              <input
                                className={styles.capCheckbox}
                                type="checkbox"
                                checked={client.capabilities.includes(capability.value)}
                                disabled={busyId === client.id}
                                onChange={() => void toggleCapability(client, capability.value)}
                              />
                              {capability.label}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td data-label="Manage">
                        <div className={styles.manageActions}>
                          <button
                            type="button"
                            className={styles.rowButton}
                            onClick={() => setEditingClient(client)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={styles.rowButton}
                            onClick={() => setContactsClient(client)}
                          >
                            Contacts
                          </button>
                          <button
                            type="button"
                            className={styles.rowButton}
                            onClick={() => setPortalClient(client)}
                          >
                            Portal access
                          </button>
                        </div>
                      </td>
                      </tr>
                      <tr className={styles.contactsRow}>
                        <td colSpan={4} data-label="Contacts">
                          {client.contacts.length > 0 ? (
                            <ol className={styles.contactList}>
                              {client.contacts.map((contact) => (
                                <li key={contact.id} className={styles.contactItem}>
                                  <span className={styles.contactName}>
                                    {contact.displayName}
                                  </span>
                                  <span className={styles.contactDetail}>
                                    {contact.email || "no email"}
                                  </span>
                                  <span className={styles.contactDetail}>
                                    {contact.phone || "no phone"}
                                  </span>
                                  {contact.notes ? (
                                    <span className={styles.contactNotes} title={contact.notes}>
                                      {contact.notes}
                                    </span>
                                  ) : null}
                                </li>
                              ))}
                            </ol>
                          ) : (
                            <span className={styles.contactEmpty}>No contacts yet</span>
                          )}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {creatingClient && (
        <Modal title="New client" onClose={() => setCreatingClient(false)}>
          <div className={styles.formHost}>
            <ClientForm
              heading="Client details"
              saveClient={createClient}
              onSaved={handleClientCreated}
              onCancel={() => setCreatingClient(false)}
            />
          </div>
        </Modal>
      )}

      {editingClient && (
        <Modal
          title="Edit client"
          subtitle={editingClient.name}
          onClose={() => setEditingClient(null)}
        >
          <div className={styles.formHost}>
            <ClientForm
              key={editingClient.id}
              saveClient={(draft) => updateClient(editingClient.id, draft).then((r) => r.client)}
              initial={{
                legalName: editingClient.name,
                phone: editingClient.phone,
                email: editingClient.email,
                address: editingClient.address,
                notes: editingClient.notes,
                abnNumber: editingClient.abnNumber,
                acnNumber: editingClient.acnNumber,
                type: editingClient.type,
              }}
              heading="Client details"
              submitLabel="Save changes"
              onSaved={handleClientUpdated}
              onCancel={() => setEditingClient(null)}
            />
          </div>
        </Modal>
      )}

      {portalClient && (
        <PortalUsersModal client={portalClient} onClose={() => setPortalClient(null)} />
      )}
      {contactsClient && (
        <ClientContactsModal
          client={contactsClient}
          onClose={() => {
            setContactsClient(null);
            // The inline contact list reads from the client records, so any
            // add/edit/delete done in the dialog must refresh the table.
            void loadClients();
          }}
        />
      )}
    </div>
  );
}
